import { SpecialtyIds, ProcedureIds } from '../../../constants';
import { ColloquialSpecialtyIds } from './constants';
import { find, includes } from 'lodash';

function doesProviderPerformAllProcedures(providerProcedures, procedures) {
    if (!providerProcedures) {
        return false;
    }

    const providerProcedureIds = providerProcedures.map(procedure => procedure.id);
    let doesPerformAllProcedures = true;

    procedures.forEach(procedure => {
        if (!providerProcedureIds.includes(procedure.id)) {
            doesPerformAllProcedures = false;
        }
    });

    return doesPerformAllProcedures;
}

export function checkRefineProcedureConstraint({
    orthopedicProcedures = [],
    providerRelevantSpecialtyId,
    providerProcedures = [],
    searchProcedureId,
}) {
    const isOrthopedicConsultationSearch = searchProcedureId === ProcedureIds.ORTHOPEDIC_CONSULTATION;
    const doesProviderPerformAllOrthopedicProcedures = doesProviderPerformAllProcedures(
        providerProcedures, orthopedicProcedures
    );

    const isPlasticSurgeryConsultationSearch = searchProcedureId === ProcedureIds.PLASTIC_SURGERY_CONSULTATION;
    const isProviderAFacialPlasticSurgeon = providerRelevantSpecialtyId === SpecialtyIds.FACIAL_PLASTIC_SURGEON;

    return (isOrthopedicConsultationSearch && !doesProviderPerformAllOrthopedicProcedures)
        || (isPlasticSurgeryConsultationSearch && isProviderAFacialPlasticSurgeon);
}

export function checkUrgentCareConstraint({
    providerIsUrgentCare,
    providerSpecialties,
    searchSpecialtyId,
}) {
    const isEMP = Boolean(find(providerSpecialties, { id: SpecialtyIds.EMERGENCY_MEDICINE_PHYSICIAN }));
    const searchedForPCP = searchSpecialtyId === SpecialtyIds.PRIMARY_CARE_PHYSICIAN;

    return (isEMP && searchedForPCP) || providerIsUrgentCare;
}

export function checkObgynConstraint({
    providerSpecialties,
    searchProcedureId,
    obgynProcedures,
}) {
    const pcpSpecialtyIds = [
        SpecialtyIds.INTERNIST,
        SpecialtyIds.FAMILY_PHYSICIAN,
        SpecialtyIds.PRIMARY_CARE_PHYSICIAN,
    ];

    const isNotPcp = Boolean(find(providerSpecialties), specialty => {
        return includes(pcpSpecialtyIds, specialty.id);
    });
    const isObgyn = Boolean(find(providerSpecialties, { id: SpecialtyIds.OBGYN }));
    const isObgynProcedure = Boolean(find(obgynProcedures, { id: searchProcedureId }));

    return (isNotPcp && isObgyn && !isObgynProcedure);
}

export function checkPediatricConstraint({
    isProfile,
    providerOnlySeesChildren,
    searchSpecialtyId,
}) {
    const isNotPediatricSearch = searchSpecialtyId !== SpecialtyIds.PEDIATRICIAN
        && searchSpecialtyId !== SpecialtyIds.PEDIATRIC_DENTIST;

    return ((isProfile || isNotPediatricSearch) && providerOnlySeesChildren);
}

export function checkNaturopathicConstraint({
    searchSpecialtyId = {},
    providerSpecialties,
    isProfile,
}) {
    const isNotNaturopathicSearch = searchSpecialtyId !== SpecialtyIds.NATUROPATHIC;
    const isNaturopathApprovedDr = Boolean(find(providerSpecialties, { id: SpecialtyIds.NATUROPATHIC }));

    return (!isProfile && isNotNaturopathicSearch && isNaturopathApprovedDr);
}

export function checkMayChargeAdditionalCopayConstraint({
    providerMayChargeAdditionalCopay,
}) {
    return providerMayChargeAdditionalCopay;
}

export function checkPhysicianAssistantConstraint({
    providerRelevantSpecialtyId,
    isProfile,
}) {
    return !isProfile && providerRelevantSpecialtyId === SpecialtyIds.PHYSICIAN_ASSISTANT;
}

export function checkCrossListedConstraint({
    searchSpecialtyId,
    providerRelevantSpecialtyId,
}) {
    // specialtyId not specified in search - probably coming from profile page
    if (!searchSpecialtyId || searchSpecialtyId === '-1') {
        return false;
    }

    // if it's a generic patient search term, don't show the popup
    if (includes(ColloquialSpecialtyIds, searchSpecialtyId)) {
        return false;
    }

    return !areSpecialtiesEquivalentInSearch(searchSpecialtyId, providerRelevantSpecialtyId);
}

export function areSpecialtiesEquivalentInSearch(
    searchSpecialtyId,
    relevantSpecialtyId,
) {
    if (searchSpecialtyId === relevantSpecialtyId) {
        return true;
    }

    /* Avoid different permutations by enforcing searchSpecialty always less than relevantSpecialtyId */
    if (parseInt(searchSpecialtyId, 10) > parseInt(relevantSpecialtyId, 10)) {
        const tmp = searchSpecialtyId;
        searchSpecialtyId = relevantSpecialtyId;
        relevantSpecialtyId = tmp;
    }

    switch (searchSpecialtyId) {
        case SpecialtyIds.INTERNIST:
            return relevantSpecialtyId === SpecialtyIds.FAMILY_PHYSICIAN
                || relevantSpecialtyId === SpecialtyIds.PRIMARY_CARE_PHYSICIAN;
        case SpecialtyIds.FAMILY_PHYSICIAN:
            return relevantSpecialtyId === SpecialtyIds.PRIMARY_CARE_PHYSICIAN;
        case SpecialtyIds.PLASTIC_SURGEON:
            return relevantSpecialtyId === SpecialtyIds.FACIAL_PLASTIC_SURGEON;
        case SpecialtyIds.ALLERGIST:
            return relevantSpecialtyId === SpecialtyIds.IMMUNOLOGIST;
        default:
            return false;
    }
}
